import { useCallback, useEffect, useRef, useState } from 'react';
import { getRealtimeToken } from '@services/realtime.service';
import { useEvent } from '@hooks/useEvent';
import { EventType } from '@enums/eventType';

export interface ProcessingEvent {
    count: number;
    lastReportId: number | null;
    lastReportStatus: 'ok' | 'error' | null;
    ts: number;
}

interface Options {
    enabled: boolean;
    onMessage: (event: ProcessingEvent) => void;
    onFallback: () => void;
}

const MAX_ERROR_COUNT = 3;

function useRealtimeReportsSubscription({ enabled, onMessage, onFallback }: Options) {
    const [connected, setConnected] = useState(false);
    const esRef = useRef<EventSource | null>(null);
    const errorCountRef = useRef(0);
    const fallbackActiveRef = useRef(false);
    const cancelledRef = useRef(false);

    const onMessageRef = useRef(onMessage);
    const onFallbackRef = useRef(onFallback);
    useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
    useEffect(() => { onFallbackRef.current = onFallback; }, [onFallback]);

    const { listen, remove } = useEvent();

    const closeSse = useCallback(() => {
        if (esRef.current) {
            esRef.current.close();
            esRef.current = null;
        }
        setConnected(false);
    }, []);

    const triggerFallback = useCallback(() => {
        closeSse();
        if (!fallbackActiveRef.current) {
            fallbackActiveRef.current = true;
            onFallbackRef.current();
        }
    }, [closeSse]);

    const connect = useCallback(async () => {
        if (cancelledRef.current) {
            return;
        }
        if (esRef.current) {
            closeSse();
        }

        let tokenData;
        try {
            tokenData = await getRealtimeToken();
        } catch (err) {
            triggerFallback();
            return;
        }

        if (cancelledRef.current) {
            return;
        }

        if (!tokenData?.hubUrl || !tokenData?.topics?.length || !tokenData?.token) {
            triggerFallback();
            return;
        }

        let url: URL;
        try {
            url = new URL(tokenData.hubUrl);
            tokenData.topics.forEach(topic => url.searchParams.append('topic', topic));
            url.searchParams.append('authorization', tokenData.token);
        } catch (err) {
            triggerFallback();
            return;
        }

        if (typeof window === 'undefined' || !window.EventSource) {
            triggerFallback();
            return;
        }

        const es = new EventSource(url.toString());
        esRef.current = es;
        errorCountRef.current = 0;

        es.onopen = () => {
            errorCountRef.current = 0;
            setConnected(true);
            fallbackActiveRef.current = false;
        };

        es.onmessage = (event) => {
            errorCountRef.current = 0;
            try {
                const data = JSON.parse(event.data);
                onMessageRef.current({
                    count: data.count ?? 0,
                    lastReportId: data.lastReportId ?? null,
                    lastReportStatus: data.lastReportStatus ?? null,
                    ts: data.ts ?? Date.now() / 1000,
                });
            } catch (err) {
                console.error(err);
            }
        };

        es.onerror = () => {
            errorCountRef.current += 1;
            setConnected(false);
            if (errorCountRef.current >= MAX_ERROR_COUNT) {
                triggerFallback();
            }
        };
    }, [closeSse, triggerFallback]);

    useEffect(() => {
        if (!enabled) {
            cancelledRef.current = true;
            closeSse();
            return;
        }

        cancelledRef.current = false;
        fallbackActiveRef.current = false;
        connect();

        const handleVisibility = () => {
            if (document.visibilityState === 'visible' && !fallbackActiveRef.current) {
                connect();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        const handleSubscriptionRefresh = () => {
            cancelledRef.current = false;
            fallbackActiveRef.current = false;
            errorCountRef.current = 0;
            connect();
        };
        listen(EventType['REALTIME.SUBSCRIPTION.REFRESH'], handleSubscriptionRefresh);

        return () => {
            cancelledRef.current = true;
            document.removeEventListener('visibilitychange', handleVisibility);
            remove(EventType['REALTIME.SUBSCRIPTION.REFRESH'], handleSubscriptionRefresh);
            closeSse();
        };
    }, [enabled, connect, closeSse]);

    return { connected };
}

export default useRealtimeReportsSubscription;
